import React, { useState } from 'react';
import './HelpScreen.css';

const HelpScreen = ({ onAction }) => {
  const [activeView, setActiveView] = useState('menu'); // 'menu', 'rules', 'controls', 'modes'

  // Render help menu with topic selection
  const renderHelpMenu = () => (
    <div className="stats-menu">
      <div className="stats-menu-buttons">
        <button 
          className="stats-menu-btn pvp-btn"
          onClick={() => setActiveView('rules')}
        >
          <div className="btn-content">
            <h3>Game Rules</h3>
            <p>How to play Connect 4</p>
          </div>
        </button>
        
        <button 
          className="stats-menu-btn highscore-btn"
          onClick={() => setActiveView('modes')}
        >
          <div className="btn-content">
            <h3>Game Modes</h3>
            <p>Different ways to play</p>
          </div>
        </button>
      </div>
    </div>
  );

  // Render game rules section
  const renderGameRules = () => (
    <div className="stats-section pvp-stats">
      <div className="stats-section-header">
        <h2>
          <span className="section-icon">📖</span>
          Game Rules
        </h2>
      </div>
      
      <div className="help-content">
        <div className="help-section">
          <h3>🎯 Objective</h3>
          <p>Be the first player to connect <strong>four</strong> of your colored discs in a row - horizontally, vertically, or diagonally.</p>
        </div>

        <div className="help-section">
          <h3>🎮 How to Play</h3>
          
          {/* Desktop Instructions */}
          <div className="platform-instructions">
            <h4>💻 On Desktop/Laptop</h4>
            <div className="help-steps">
              <div className="help-step">
                <span className="step-number">1</span>
                <div className="step-content">
                  <h4>Choose Your Column</h4>
                  <p>Click on any column or press number keys <strong>1-7</strong> to drop your disc.</p>
                </div>
              </div>
              <div className="help-step">
                <span className="step-number">2</span>
                <div className="step-content">
                  <h4>Disc Falls Down</h4>
                  <p>Your disc will fall to the lowest available position in that column with gravity animation.</p>
                </div>
              </div>
              <div className="help-step">
                <span className="step-number">3</span>
                <div className="step-content">
                  <h4>Take Turns</h4>
                  <p>Players alternate turns. <span className="red-text">Red</span> goes first, then <span className="yellow-text">Yellow</span>.</p>
                </div>
              </div>
              <div className="help-step">
                <span className="step-number">4</span>
                <div className="step-content">
                  <h4>Connect Four!</h4>
                  <p>First to connect 4 discs in a row wins the game! 🏆</p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Instructions */}
          <div className="platform-instructions">
            <h4>📱 On Mobile/Tablet</h4>
            <div className="help-steps">
              <div className="help-step">
                <span className="step-number">1</span>
                <div className="step-content">
                  <h4>Tap to Play</h4>
                  <p>Simply <strong>tap</strong> on any column where you want to drop your disc.</p>
                </div>
              </div>
              <div className="help-step">
                <span className="step-number">2</span>
                <div className="step-content">
                  <h4>Watch It Fall</h4>
                  <p>The disc will automatically fall to the lowest available spot with smooth animation.</p>
                </div>
              </div>
              <div className="help-step">
                <span className="step-number">3</span>
                <div className="step-content">
                  <h4>Alternate Turns</h4>
                  <p>Take turns with your opponent. The game shows whose turn it is at the top.</p>
                </div>
              </div>
              <div className="help-step">
                <span className="step-number">4</span>
                <div className="step-content">
                  <h4>Win the Game!</h4>
                  <p>Connect 4 discs in any direction to claim victory! 🎉</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="help-section">
          <h3>🏆 Winning Conditions</h3>
          <div className="stats-grid">
            <div className="stat-card help-card">
              <div className="help-icon">➡️</div>
              <h4>Horizontal</h4>
              <p>Four in a row <strong>horizontally</strong></p>
            </div>
            <div className="stat-card help-card">
              <div className="help-icon">⬇️</div>
              <h4>Vertical</h4>
              <p>Four in a column <strong>vertically</strong></p>
            </div>
            <div className="stat-card help-card">
              <div className="help-icon">↘️</div>
              <h4>Diagonal</h4>
              <p>Four in a <strong>diagonal</strong> line</p>
            </div>
            <div className="stat-card help-card">
              <div className="help-icon">🤝</div>
              <h4>Draw</h4>
              <p>Board full with <strong>no winner</strong></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render controls and features section
  const renderControlsFeatures = () => (
    <div className="stats-section online-stats">
      <div className="stats-section-header">
        <h2>
          <span className="section-icon">🎮</span>
          Controls & Features
        </h2>
      </div>
      
      <div className="help-content">
        <div className="help-section">
          <h3>⌨️ Keyboard Controls</h3>
          <p className="section-desc">Master these shortcuts for faster gameplay!</p>
          <div className="controls-grid">
            <div className="control-item">
              <span className="control-key">1-7</span>
              <span className="control-desc">Drop disc in column 1-7</span>
            </div>
            <div className="control-item">
              <span className="control-key">P</span>
              <span className="control-desc">Pause/Resume game</span>
            </div>
            <div className="control-item">
              <span className="control-key">R</span>
              <span className="control-desc">Restart current game</span>
            </div>
            <div className="control-item">
              <span className="control-key">ESC</span>
              <span className="control-desc">Return to menu</span>
            </div>
            <div className="control-item">
              <span className="control-key">H</span>
              <span className="control-desc">Open help screen</span>
            </div>
            <div className="control-item">
              <span className="control-key">S</span>
              <span className="control-desc">View statistics</span>
            </div>
          </div>
        </div>

        <div className="help-section">
          <h3>✨ Game Features</h3>
          <p className="section-desc">Everything you need for an amazing gaming experience!</p>
          <div className="features-list">
            <div className="feature-item">
              <span className="feature-icon">⏱️</span>
              <div className="feature-content">
                <h4>Precision Game Timer</h4>
                <p>Track your game time with <strong>minutes:seconds:milliseconds</strong> precision for competitive play</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📊</span>
              <div className="feature-content">
                <h4>Complete Statistics Tracking</h4>
                <p>Detailed stats for all game modes including <strong>fastest wins</strong>, records, and win rates</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🎨</span>
              <div className="feature-content">
                <h4>Custom Player Names</h4>
                <p>Personalize your experience by setting <strong>unique names</strong> for both players in local multiplayer</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">💾</span>
              <div className="feature-content">
                <h4>Auto-Save Progress</h4>
                <p>Your stats, settings, and achievements are <strong>automatically saved</strong> to your browser</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render game modes section
  const renderGameModes = () => (
    <div className="stats-section pvc-stats">
      <div className="stats-section-header">
        <h2>
          <span className="section-icon">🎯</span>
          Game Modes
        </h2>
      </div>
      
      <div className="help-content">
        <div className="help-section">
          <h3>🎲 Available Game Modes</h3>
          <p className="section-desc">Choose your preferred way to play Connect 4!</p>
          
          <div className="mode-card">
            <div className="mode-header">
              <span className="mode-icon">👥</span>
              <h4>Player vs Player (Local)</h4>
            </div>
            <p className="mode-description">
              Play against a friend on the <strong>same device</strong>. Take turns dropping discs and compete 
              for the fastest win time. Perfect for local multiplayer fun with friends and family!
            </p>
            <div className="mode-features">
              <span className="mode-feature">✓ Custom player names</span>
              <span className="mode-feature">✓ Fastest/slowest win records</span>
              <span className="mode-feature">✓ Win statistics tracking</span>
            </div>
          </div>

          <div className="mode-card">
            <div className="mode-header">
              <span className="mode-icon">🤖</span>
              <h4>Player vs Computer</h4>
            </div>
            <p className="mode-description">
              Challenge the AI with <strong>three difficulty levels</strong>. Test your skills and try to 
              achieve the fastest win times against each difficulty level!
            </p>
            <div className="difficulty-info">
              <div className="difficulty-level">
                <span className="difficulty-icon">🎮</span>
                <div>
                  <h5> ROOKIE AI</h5>
                  <p>Perfect for <strong>beginners</strong> - AI makes simple, random moves. Great for learning!</p>
                </div>
              </div>
              <div className="difficulty-level">
                <span className="difficulty-icon">⚔️</span>
                <div>
                  <h5> WARRIOR AI</h5>
                  <p><strong>Moderate challenge</strong> - AI thinks ahead strategically. Good for intermediate players!</p>
                </div>
              </div>
              <div className="difficulty-level">
                <span className="difficulty-icon">👑</span>
                <div>
                  <h5> MASTER AI</h5>
                  <p><strong>Expert level</strong> - Maximum strategic depth. Can you beat the master?</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mode-card">
            <div className="mode-header">
              <span className="mode-icon">🌐</span>
              <h4>Online Multiplayer</h4>
            </div>
            <p className="mode-description">
              Connect with players <strong>around the world</strong>! Create or join rooms to play Connect 4 
              online with <strong>real-time gameplay</strong>. Experience the thrill of global competition!
            </p>
            <div className="mode-features">
              <span className="mode-feature">✓ Real-time online matches</span>
              <span className="mode-feature">✓ Room-based matchmaking</span>
              <span className="mode-feature">✓ Global win/loss tracking</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="statistics-screen">
      <div className="stats-container">
        <div className="stats-header">
          <h1>Help</h1>
          <div className="title-decoration">
            <div className="piece red"></div>
            <div className="piece yellow"></div>
            <div className="piece red"></div>
            <div className="piece yellow"></div>
          </div>
        </div>
        
        <div className="stats-content">
          {activeView === 'menu' && renderHelpMenu()}
          {activeView === 'rules' && renderGameRules()}
          {activeView === 'controls' && renderControlsFeatures()}
          {activeView === 'modes' && renderGameModes()}
        </div>
        
        <div className="stats-actions">
          <button 
            className="back-button"
            onClick={() => onAction('menu')}
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpScreen;